import React from 'react'
import {
  Typography,
  Paper,
  Grid,
  Button,
  Box,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
} from '@material-ui/core'
import Menu from '@material-ui/core/Menu'
import AddIcon from '@material-ui/icons/Add'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import QuillTextEditor from '../TextEditorToolbar/TextEditor'
import EditTestCase from '../EditTestCase/EditTestCase'
import Loader from '../Loader/Loader'
import UpdateQuestionsInQuizesModal from '../UpdateQuestionInQuizesModal/UpdateQuestionInQuizesModal'
import { useStyles } from './AddCodingQuestionStyles'

const LANGUAGE_LIST = ['golang', 'java', 'python', 'c', 'cpp', 'javascript']

const ALLOWED_INPUT_TYPES = [
  'integer',
  'double',
  'string',
  'integer array',
  'double array',
  'string array',
]

const RETURN_TYPE_LIST = [
  { value: 'input1', label: 'integer' },
  { value: 'input2', label: 'double' },
  { value: 'input3', label: 'string' },
  { value: 'input4', label: 'bool' },
]

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const EditCodingQuestion = ({
  addQuestion,
  questions,
  questionIndex,
  quizId,
  localQuestion,
  textEditorQuestion,
  setTextEditorQuestion,
  errors,
  isQuizStarted,
  quizStartTime,
  isPublished,
  questionTypeFromParent,
  handleQuestionTypeChange,
  handleEdit,
  handleChange,
  handleChangeLanguage,
  handleChangeReturnType,
  handleFunctionName,
  handleAddInputParam,
  handleRemoveInputParam,
  handleChoice,
  handleRemoveChoice,
  handleSave,
  handleCancel,
  handleCancelCode,
  handleSaveButtonClick,
  calculateCodingMarks,
  normalizeInputTypes,
  anchorEl,
  handleOpenMenu,
  handleCloseMenu,
  editQuestionQuery,
  postQuestion,
  showQuizesListModal,
  setShowQuizesListModal,
  affectedQuizes,
  updateQuestionInSelectedQuizzes,
  setSelectedQuizzes,
  selectedQuizzes,
  invalidTestCaseInput,
  invalidTestCaseArguments,
  editQuestion,
  testCaseCounts,
  hasRequiredTestCases,
  outputPlaceholder,
}) => {
  const classes = useStyles()
  const isQuizActive = isQuizStarted(quizStartTime) && isPublished
  const inputParams = normalizeInputTypes(
    localQuestion?.codeStubConfigs?.inputTypes
  )

  return (
    <Grid style={{ display: 'flex' }}>
      <Grid item xs={10} sm={11} style={{ paddingRight: '16px' }}>
        <Paper className={classes.editPaper} variant="outlined">
          <Grid item container xs={12} style={{ paddingBottom: '10px' }}>
            <UpdateQuestionsInQuizesModal
              isLoading={false}
              showQuizesListModal={showQuizesListModal}
              setShowQuizesListModal={setShowQuizesListModal}
              affectedQuizes={affectedQuizes}
              handleSave={handleSave}
              currentQuestion={localQuestion}
              updateQuestionInSelectedQuizzes={updateQuestionInSelectedQuizzes}
              setSelectedQuizzes={setSelectedQuizzes}
              selectedQuizzes={selectedQuizzes}
              quizId={quizId}
            />

            <Grid
              item
              xs={12}
              sm={4}
              style={{ alignItems: 'end', display: 'flex' }}
            >
              <Typography className={classes.questionIndex}>
                Question{' '}
                {addQuestion ? questions?.length + 1 : questionIndex + 1}.
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              sm={8}
              container
              className={classes.marksType}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Tooltip
                placement="top"
                arrow
                classes={{ arrow: classes.arrow, tooltip: classes.tooltip }}
                title="This is an aggregate of testcases marks given below"
              >
                <TextField
                  style={{ pointerEvent: 'none', cursor: 'none' }}
                  disabled
                  id="outlined-basic"
                  value={calculateCodingMarks()}
                  variant="outlined"
                  autoComplete="off"
                  size="small"
                  error={
                    errors?.emptyMarks ||
                    errors?.marksNotValid ||
                    errors?.marksLessThanZero
                  }
                  className={classes.mcqMarksContainer}
                  inputProps={{
                    style: {
                      fontSize: 16,
                      color: '#262B4B',
                      textAlign: 'center',
                      height: '32px',
                      width: '40px',
                      pointerEvents: 'none',
                    },
                  }}
                  onChange={(event) =>
                    handleEdit(event, 'questionWeightage', 0)
                  }
                  label="Marks"
                />
              </Tooltip>

              {addQuestion && (
                <FormControl variant="outlined" style={{ marginLeft: '8px' }}>
                  <InputLabel id="question-type-label">
                    Question Type
                  </InputLabel>
                  <Select
                    labelId="question-type-label"
                    id="question-type-select"
                    value={questionTypeFromParent}
                    onChange={handleQuestionTypeChange}
                    label="Question Type"
                  >
                    <MenuItem value="mcq">Multiple Choice Question</MenuItem>
                    <MenuItem value="coding">Coding Question</MenuItem>
                    <MenuItem value="t/f">True/False Question</MenuItem>
                    <MenuItem value="multi-choice">
                      Multiple Answer Question
                    </MenuItem>
                    <MenuItem value="essay">Essay Question</MenuItem>
                    <MenuItem value="fillInBlanks">Fill In The Blanks</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>

          <Grid container className={classes.editQuestion}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box>
                  <Box display="flex">
                    <Box flexGrow={1}>
                      <QuillTextEditor
                        key={localQuestion?.id?.toString()}
                        textEditorQuestion={textEditorQuestion}
                        setTextEditorQuestion={setTextEditorQuestion}
                        className={localQuestion?.id?.toString()}
                        isQuizStarted={isQuizActive}
                      />
                    </Box>
                  </Box>
                  <Grid
                    container
                    direction="row"
                    className={classes.marksQuestionValidationContainer}
                  >
                    <Grid item sm={8} xs={12}>
                      {errors?.emptyQuestion && (
                        <Typography className={classes.validationText}>
                          Question should not be empty
                        </Typography>
                      )}
                    </Grid>
                    <Grid
                      item
                      sm={4}
                      xs={12}
                      className={classes.marksValidationContainer}
                    >
                      {errors?.emptyWeightage && (
                        <Typography className={classes.validationText}>
                          Marks should not be empty
                        </Typography>
                      )}
                      {errors?.weightageLessThanZero && (
                        <Typography className={classes.validationText}>
                          Marks should be more than 0
                        </Typography>
                      )}
                      {errors?.weightageNotValid && (
                        <Typography className={classes.validationText}>
                          Only numeric values are allowed
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} className={classes.codeSnippetContainer}>
                <div className={classes.paper} style={{ padding: '0px' }}>
                  <div className={classes.codeDetails}>
                    <Grid item xs={12}>
                      <Typography className={classes.codeSnippetTitle}>
                        Configurations:
                      </Typography>
                    </Grid>

                    <div className={classes.codeSnippetLanguagesMenu}>
                      <div className={classes.languageWrapper}>
                        <Typography className={classes.codeLanguageMessage}>
                          Languages
                        </Typography>
                        <FormControl
                          className={classes.languageForm}
                          variant="outlined"
                          fullWidth
                          style={{ marginTop: '-20px' }}
                        >
                          <Autocomplete
                            multiple
                            id="language-autocomplete"
                            options={LANGUAGE_LIST}
                            disableCloseOnSelect
                            onChange={handleChangeLanguage}
                            value={
                              localQuestion?.codeStubConfigs?.languages || []
                            }
                            getOptionLabel={(option) => option}
                            renderOption={(option, { selected }) => (
                              <Box>
                                <Checkbox
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option}
                              </Box>
                            )}
                            size="small"
                            renderInput={(params) => (
                              <TextField {...params} variant="outlined" />
                            )}
                          />
                        </FormControl>
                        {localQuestion?.codeStubConfigs?.languages?.length ===
                          0 && (
                          <Typography className={classes.validationText}>
                            Select at least one language
                          </Typography>
                        )}
                      </div>

                      <div style={{ flex: 1 }} />

                      <div className={classes.inputWrapper}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{ marginBottom: '8px' }}
                        >
                          <Typography className={classes.inputTypesHeader}>
                            Input Parameters
                            <span
                              style={{
                                fontSize: '11px',
                                color: '#999',
                                marginLeft: '8px',
                                fontWeight: 400,
                              }}
                            >
                              ({inputParams.length}/10)
                            </span>
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          flexWrap="wrap"
                          alignItems="center"
                          style={{ gap: '10px', minHeight: '42px' }}
                        >
                          {inputParams.map((param, index) => (
                            <Chip
                              key={index}
                              label={`param${index + 1}: ${param.type}`}
                              onDelete={() => handleRemoveInputParam(index)}
                              style={{
                                backgroundColor: '#E0F7F5',
                                color: '#00B7A4',
                                fontWeight: 600,
                                fontSize: '13px',
                                height: '36px',
                                border: '1px solid #00B7A4',
                                borderRadius: '18px',
                                padding: '0 4px',
                              }}
                            />
                          ))}

                          {inputParams.length < 10 && (
                            <Box
                              style={{
                                position: 'relative',
                                display: 'inline-flex',
                              }}
                            >
                              <Button
                                onClick={handleOpenMenu}
                                variant="contained"
                                style={{
                                  height: '35px',
                                  borderRadius: '18px',
                                  backgroundColor: '#00B7A4',
                                  color: '#ffffff',
                                  fontWeight: 600,
                                  fontSize: '18px',
                                  textTransform: 'none',
                                  padding: '0 14px',
                                  minWidth: '80px',
                                  boxShadow: 'none',
                                  display: 'flex',
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    '#009688'
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    '#00B7A4'
                                }}
                              >
                                + Add
                              </Button>

                              <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'left',
                                }}
                              >
                                {ALLOWED_INPUT_TYPES.map((t) => (
                                  <MenuItem
                                    key={t}
                                    onClick={() => {
                                      handleAddInputParam(t)
                                      handleCloseMenu()
                                    }}
                                  >
                                    {t}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </Box>
                          )}

                          {inputParams.length >= 10 && (
                            <Typography
                              style={{ fontSize: '12px', color: '#999' }}
                            >
                              Max 10 reached
                            </Typography>
                          )}
                        </Box>

                        {inputParams.length === 0 && (
                          <Typography
                            style={{
                              fontSize: '12px',
                              color: '#999',
                              marginTop: '6px',
                            }}
                          >
                            No parameters yet. Click &ldquo;+ Add&rdquo; to get
                            started.
                          </Typography>
                        )}

                        {errors?.emptyInputTypes && (
                          <Typography className={classes.validationText}>
                            Add at least one input parameter
                          </Typography>
                        )}
                        {invalidTestCaseInput?.length > 0 && (
                          <Typography
                            style={{ fontSize: '12px', color: 'red' }}
                          >
                            Input types and test case inputs must have matching
                            types
                          </Typography>
                        )}
                      </div>
                    </div>

                    <div className={classes.funcAndReturnType}>
                      <div className={classes.functionWrapper}>
                        <Typography className={classes.codeLanguageMessage}>
                          Function name
                        </Typography>
                        <TextField
                          fullWidth
                          label="Enter name"
                          defaultValue={
                            localQuestion?.codeStubConfigs?.functionName
                          }
                          variant="outlined"
                          onChange={handleFunctionName}
                          className={classes.functionTextField}
                          size="small"
                        />
                        {errors?.emptyFunction && (
                          <Typography className={classes.validationText}>
                            Function name cannot be empty
                          </Typography>
                        )}
                        {errors?.invalidFunctionName && (
                          <Typography className={classes.validationText}>
                            Function name cannot have whitespace.
                          </Typography>
                        )}
                      </div>

                      <div style={{ flex: 1 }} />

                      <div className={classes.returnTypeWrapper}>
                        <Typography className={classes.returnTypeHeader}>
                          Return type
                        </Typography>
                        <FormControl
                          variant="outlined"
                          fullWidth
                          className={classes.returnType}
                          size="small"
                        >
                          <InputLabel id="return-type-label">Select</InputLabel>
                          <Select
                            labelId="return-type-label"
                            id="return-type-select"
                            value={
                              localQuestion?.codeStubConfigs?.returnType ?? ''
                            }
                            onChange={handleChangeReturnType}
                            label="Select"
                          >
                            {RETURN_TYPE_LIST.map((option) => (
                              <MenuItem key={option.value} value={option.label}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div
                  className={classes.paper}
                  style={{ paddingRight: 0, paddingLeft: 0 }}
                >
                  <div className={classes.testCaseHeadingForMobile}>
                    <Typography variant="h4">Test Cases</Typography>
                  </div>

                  <Box
                    style={{
                      backgroundColor: '#FFF3CD',
                      border: '1px solid #FFE69C',
                      borderRadius: '4px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: '14px',
                        color: '#664D03',
                        fontWeight: 500,
                      }}
                    >
                      ⚠️ At least 1 visible (sample) test case and 1 hidden test
                      case are required to save the question.
                    </Typography>
                    <Box
                      style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        gap: '16px',
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: '13px',
                          color:
                            testCaseCounts.sample >= 1 ? '#198754' : '#DC3545',
                          fontWeight: 500,
                        }}
                      >
                        Visible: {testCaseCounts.sample}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '13px',
                          color:
                            testCaseCounts.hidden >= 1 ? '#198754' : '#DC3545',
                          fontWeight: 500,
                        }}
                      >
                        Hidden: {testCaseCounts.hidden}
                      </Typography>
                    </Box>
                  </Box>

                  <div className={classes.textBoxContainer}>
                    <div className={classes.testCaseNumbers}>SN</div>
                    <div className={classes.testCaseInputTitle}>Input</div>
                    <div className={classes.testCaseOutputTitle}>Output</div>
                    <div className={classes.testCaseMarks}>Marks</div>
                    <div className={classes.hiddenCaseTitle}>Hidden</div>
                  </div>

                  {localQuestion?.testcases?.map((testcase, index) => (
                    <EditTestCase
                      key={testcase.id}
                      testcase={testcase}
                      index={index}
                      handleChange={handleChange}
                      handleRemoveChoice={handleRemoveChoice}
                      handleEdit={handleEdit}
                      isQuizStarted={isQuizActive}
                      editQuestion={editQuestion}
                      addQuestion={addQuestion}
                      codingQuestionError={errors}
                      invalidTestCaseArguments={invalidTestCaseArguments}
                      questionIndex={questionIndex}
                      outputPlaceholder={outputPlaceholder}
                    />
                  ))}

                  {!isQuizActive && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleChoice('Add')}
                      className={classes.addTestCaseButton}
                    >
                      Add Testcase
                    </Button>
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                  }}
                >
                  {editQuestionQuery?.isLoading || postQuestion?.isLoading ? (
                    <div className={classes.loader} data-testid="render-loader">
                      <Loader size={30} />
                    </div>
                  ) : (
                    <>
                      <Button
                        onClick={
                          !Number.isNaN(editQuestion)
                            ? handleCancel
                            : handleCancelCode
                        }
                        className={classes.cancelButton}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={
                          !Number.isNaN(editQuestion)
                            ? handleSaveButtonClick
                            : handleSave
                        }
                        disabled={
                          isQuizActive ||
                          errors?.invalidInputType ||
                          errors?.invalidInputArgLength ||
                          errors?.emptyInputTypesType ||
                          invalidTestCaseInput?.length ||
                          !hasRequiredTestCases
                        }
                      >
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default EditCodingQuestion
